import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import ClassificacaoQuestao, {
  validarClassificacao,
} from '../Catalog/ClassificacaoQuestao';
import { classificarEmMassa } from '../../services/classificacao';
import CheckboxPropagacao from '../Catalog/CheckboxPropagacao';

/**
 * EG-41 — CA06 e CA08: classificação em massa.
 *
 * Grava pelo endpoint de lote, fora do save do template. O motivo é o mesmo do
 * caminho individual: o save versiona e recria as questões com ids novos, e a
 * propagação do CA07 perderia o alvo.
 *
 * O lote é atômico no backend — uma questão inválida recusa tudo. Por isso os
 * erros aparecem aqui como texto do servidor, e não como sucesso parcial: não
 * existe sucesso parcial para mostrar.
 */
const ClassificacaoMassaModal = ({ isOpen, onClose, questoes = [], onAplicado }) => {
  const [selecionadas, setSelecionadas] = useState([]);
  const [classificacao, setClassificacao] = useState({
    id_pillar: null,
    id_theme: null,
    gri_ids: [],
  });
  // CA7.2: desmarcado por padrão. Propagar sobrescreve classificação de
  // questionários já respondidos — precisa ser um ato deliberado.
  const [propagar, setPropagar] = useState(false);
  const [erro, setErro] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const toast = useToast();
  // O corpo do modal rola por dentro (scrollBehavior='inside'); com uma lista
  // longa de questões, o formulário resetado fica fora da vista depois de
  // aplicar — o gestor clicou no footer e não vê a mudança lá em cima.
  const corpoRef = useRef(null);

  /**
   * Cada abertura começa do zero.
   *
   * O TemplateBuilder mantém este componente SEMPRE montado e só alterna isOpen:
   * o Chakra esconde o conteúdo, mas o useState sobrevive. Sem este reset o modal
   * reabria com o par pilar/tema do lote ANTERIOR já escolhido — e o botão
   * Aplicar acende assim que uma questão é marcada. Um clique distraído gravaria
   * em massa a classificação da rodada passada, com propagação para dezenas de
   * instâncias em questionários já respondidos.
   *
   * Zerar na ABERTURA e não no fechamento é de propósito: o modal pode ser
   * fechado por caminhos que não passam pelo onClose (desmonte do pai, ESC
   * tratado pelo Chakra), e a garantia tem que valer para todos eles.
   */
  useEffect(() => {
    if (!isOpen) return;

    setSelecionadas([]);
    setClassificacao({ id_pillar: null, id_theme: null });
    // CA7.2 de novo: propagar volta a ser um ato deliberado a cada abertura.
    setPropagar(false);
    setErro(null);
  }, [isOpen]);

  // Questão recém-adicionada ainda não tem linha no banco: não há o que
  // classificar por id, e deixá-la marcável produziria um lote com undefined.
  const selecionaveis = useMemo(
    () => questoes.filter((questao) => Boolean(questao.id_question_template)),
    [questoes]
  );

  const alternar = (id) =>
    setSelecionadas((atual) =>
      atual.includes(id) ? atual.filter((item) => item !== id) : [...atual, id]
    );

  // GRI-02: o lote agora tem três coisas aplicáveis e nenhuma é obrigatória.
  // Sem esta checagem, "Aplicar" com tudo vazio vai ao servidor só para voltar
  // 400 — a recusa é a mesma, mas o gestor descobre pelo erro em vez de pela
  // tela.
  const temAlgoParaAplicar =
    classificacao.id_pillar != null ||
    classificacao.id_theme != null ||
    (classificacao.gri_ids?.length ?? 0) > 0;

  const aplicar = async () => {
    const problema = validarClassificacao(classificacao);
    if (problema) {
      setErro(problema);
      return;
    }

    if (!temAlgoParaAplicar) {
      setErro('Escolha um pilar, um tema ou ao menos um indicador GRI.');
      return;
    }

    setEnviando(true);
    setErro(null);

    // A ordem segue a da listagem, não a da ordem de clique: torna o payload
    // previsível para quem depura pelo log.
    const ids = selecionaveis
      .map((questao) => questao.id_question_template)
      .filter((id) => selecionadas.includes(id));

    try {
      const resultado = await classificarEmMassa({
        ids,
        ...classificacao,
        propagar,
      });

      // O resultado da operação não pertence ao formulário que a disparou: o
      // toast vive fora do modal, e diz só o que o gestor precisa saber aqui —
      // passou ou não passou.
      //
      // O detalhamento (quantas propagaram, quantas ficaram fora, quantos
      // questionários mudaram de modelo de nota) foi removido por decisão do TL:
      // contagem sem identificação não habilita ação. Se essas consequências
      // precisarem de superfície, ela é uma lista com nomes, não um número.
      toast({
        title: 'Classificação aplicada',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      // A tela de trás precisa de MAIS que o resultado do servidor: sem os ids e
      // o par aplicado, ela só pode recarregar tudo — que é exatamente o
      // sintoma que o TL relatou (só o F5 mostrava a classificação).
      onAplicado?.({ resultado, ids, ...classificacao });

      // Modal fica aberto de propósito (fluxo comum é aplicar em mais de um
      // lote seguido), mas sem resetar o formulário parecia que nada tinha
      // acontecido — tudo continuava marcado/preenchido e "Aplicar" continuava
      // aceso, convidando a clicar de novo no MESMO lote. Reseta igual à
      // abertura: quem quiser aplicar outra classificação escolhe de novo, de
      // propósito — sem isso arriscaria reenviar o mesmo lote sem querer.
      setSelecionadas([]);
      setClassificacao({ id_pillar: null, id_theme: null, gri_ids: [] });
      setPropagar(false);
      corpoRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (falha) {
      // O texto do servidor fica junto ao CAMPO, porque costuma ser acionável
      // ("estas questões pertencem a uma versão removida"). O toast só responde
      // se a operação passou.
      setErro(
        falha?.response?.data?.message ||
          'Não foi possível aplicar a classificação.'
      );
      toast({
        title: 'Não foi possível aplicar a classificação',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='2xl' scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Classificar em massa</ModalHeader>
        <ModalCloseButton />

        <ModalBody ref={corpoRef}>
          <Stack spacing='16px'>
            <ClassificacaoQuestao
              valor={classificacao}
              onChange={(valor) => {
                setClassificacao(valor);
                setErro(null);
              }}
              erro={erro}
              griSoma
            />

            <CheckboxPropagacao isChecked={propagar} onChange={setPropagar} />

            <Divider />

            <Text fontSize='sm' color='gray.600'>
              {selecionadas.length} de {selecionaveis.length} questões
              selecionadas
            </Text>

            <Stack spacing='8px'>
              {questoes.map((questao, indice) => {
                const id = questao.id_question_template;
                return (
                  <Checkbox
                    key={id ?? `nova-${indice}`}
                    isDisabled={!id}
                    isChecked={selecionadas.includes(id)}
                    onChange={() => alternar(id)}
                  >
                    <Text as='span' fontWeight={600}>
                      {questao.code}
                    </Text>{' '}
                    — {questao.title}
                    {!id && (
                      <Text as='span' fontSize='xs' color='gray.500'>
                        {' '}
                        (salve o template antes de classificar)
                      </Text>
                    )}
                  </Checkbox>
                );
              })}
            </Stack>

          </Stack>
        </ModalBody>

        <ModalFooter>
          <Flex gap='12px'>
            <Button variant='ghost' onClick={onClose}>
              Fechar
            </Button>
            <Button
              colorScheme='blue'
              onClick={aplicar}
              isLoading={enviando}
              isDisabled={selecionadas.length === 0 || !temAlgoParaAplicar}
            >
              Aplicar
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ClassificacaoMassaModal;
